import React, { useEffect, useState, useMemo, createContext, useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import Home from './pages/Home/Home';
import Pricing from './pages/Pricing/Pricing';
import FAQs from './pages/FAQs/FAQs';
import Settings from './pages/Settings/Settings';
import BloodAnalysis from './pages/BloodAnalysis/BloodAnalysis';
import Onboarding from './pages/Onboarding/Onboarding';
import Auth from './pages/Auth';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { getProfile } from './utils/api/natureDrApi';
import './styles/theme.css';
import './App.css';

// Auth context for global user state
interface AuthContextValue {
  user: User | null;
  loading: boolean;
  onboardingComplete: boolean | null; // null = still checking
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  onboardingComplete: null,
  refreshProfile: async () => {},
});

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null);

  const checkProfile = async () => {
    try {
      const { profile } = await getProfile();
      setOnboardingComplete(profile?.onboardingComplete === true);
    } catch {
      setOnboardingComplete(false);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
      if (firebaseUser) {
        checkProfile();
      } else {
        setOnboardingComplete(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const value = useMemo(() => ({
    user, loading, onboardingComplete, refreshProfile: checkProfile,
  }), [user, loading, onboardingComplete]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}

// Route guard
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, onboardingComplete } = useAuth();
  const location = useLocation();
  if (loading) return <div style={{ textAlign: 'center', marginTop: '3em' }}>Loading...</div>;
  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  // Wait for profile check, then redirect to onboarding if incomplete
  if (onboardingComplete === null) return <div style={{ textAlign: 'center', marginTop: '3em' }}>Loading...</div>;
  if (!onboardingComplete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/auth" element={<Auth />} />
          <Route
            path="/onboarding"
            element={
              <RequireAuth>
                <Onboarding />
              </RequireAuth>
            }
          />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/faqs" element={<FAQs />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/blood-analysis" element={<BloodAnalysis />} />
                  </Routes>
                </Layout>
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
